import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithAuth } from './baseQuery';

// Create the letter API slice
export const letterApi = createApi({
  reducerPath: 'letterApi',
  baseQuery: createBaseQueryWithAuth(),
  tagTypes: ['LetterTemplate', 'GeneratedLetter'],

  endpoints: (builder) => ({
    // ========== LETTER TEMPLATE ENDPOINTS ========== //

    // Get all letter templates
    getLetterTemplates: builder.query({
      query: (params = {}) => ({
        url: '/letters/templates',
        params
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'LetterTemplate', id: _id })),
              { type: 'LetterTemplate', id: 'LIST' }
            ]
          : [{ type: 'LetterTemplate', id: 'LIST' }]
    }),

    // Get single letter template
    getLetterTemplate: builder.query({
      query: (id) => `/letters/templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'LetterTemplate', id }]
    }),

    // Create letter template
    createLetterTemplate: builder.mutation({
      query: (templateData) => ({
        url: '/letters/templates',
        method: 'POST',
        body: templateData
      }),
      invalidatesTags: [{ type: 'LetterTemplate', id: 'LIST' }]
    }),

    // Update letter template
    updateLetterTemplate: builder.mutation({
      query: ({ id, templateData }) => ({
        url: `/letters/templates/${id}`,
        method: 'PUT',
        body: templateData
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LetterTemplate', id },
        { type: 'LetterTemplate', id: 'LIST' }
      ]
    }),

    // Clone letter template
    cloneLetterTemplate: builder.mutation({
      query: ({ id, newName }) => ({
        url: `/letters/templates/${id}/clone`,
        method: 'POST',
        body: { newName }
      }),
      invalidatesTags: [{ type: 'LetterTemplate', id: 'LIST' }]
    }),

    // ========== GENERATED LETTER ENDPOINTS ========== //

    // Get all generated letters
    getGeneratedLetters: builder.query({
      query: (params = {}) => ({
        url: '/letters',
        params
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'GeneratedLetter', id: _id })),
              { type: 'GeneratedLetter', id: 'LIST' }
            ]
          : [{ type: 'GeneratedLetter', id: 'LIST' }]
    }),

    // Get single generated letter
    getGeneratedLetter: builder.query({
      query: (id) => `/letters/${id}`,
      providesTags: (result, error, id) => [{ type: 'GeneratedLetter', id }]
    }),

    // Generate letter
    generateLetter: builder.mutation({
      query: (letterData) => ({
        url: '/letters/generate',
        method: 'POST',
        body: letterData
      }),
      invalidatesTags: [{ type: 'GeneratedLetter', id: 'LIST' }]
    }),

    // Delete generated letter
    deleteGeneratedLetter: builder.mutation({
      query: (id) => ({
        url: `/letters/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GeneratedLetter', id },
        { type: 'GeneratedLetter', id: 'LIST' }
      ]
    }),

    // Note: Add additional endpoints as needed for future features:
    // - PDF/DOCX export
    // - Email functionality
    // - Bulk operations
    // - Template statistics
    // - Document storage/retrieval
  })
});

// Export hooks for usage in components
export const {
  // Letter template hooks
  useGetLetterTemplatesQuery,
  useGetLetterTemplateQuery,
  useCreateLetterTemplateMutation,
  useUpdateLetterTemplateMutation,
  useCloneLetterTemplateMutation,

  // Generated letter hooks
  useGetGeneratedLettersQuery,
  useGetGeneratedLetterQuery,
  useGenerateLetterMutation,
  useDeleteGeneratedLetterMutation
} = letterApi;
